import React from "react";
import { motion } from "framer-motion";
import { Quote, Home } from "lucide-react";
import { IMAGES } from "@/lib/site";

export const Philosophy = () => {
  return (
    <section
      id="philosophy"
      className="relative py-16 md:py-36 bg-ivory"
      data-testid="philosophy-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Founder image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 relative max-w-[260px] sm:max-w-sm mx-auto lg:mx-0"
          >
            <div className="relative aspect-square sm:aspect-[4/5] rounded-3xl overflow-hidden">
              <img
                src={IMAGES.founder}
                alt="Dr. Krishnakumar KV, founder of Kakkanat Ayurvedic postnatal care, Kerala"
                className="w-full h-full object-cover object-top sm:object-center"
                data-testid="founder-portrait"
              />
            </div>
            <div className="absolute -bottom-6 left-4 right-4 md:-bottom-7 bg-ivory border border-sage/15 rounded-2xl p-4 md:p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-sage">Founder</div>
              <div className="mt-1 font-serif text-lg md:text-xl text-charcoal">Dr. Krishnakumar KV</div>
              <div className="text-xs md:text-sm text-charcoal/60 mt-0.5">BAMS, MD (AYU)</div>
            </div>
          </motion.div>

          {/* Quote */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Founder's Note
            </div>
            <Quote size={36} strokeWidth={1.25} className="text-sage mt-6" />
            <h2
              className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-[1.15] tracking-tight"
              data-testid="founder-quote"
            >
              “Care is not about doing many things; it&apos;s about doing the <span className="italic text-sage-700">right things</span>.”
            </h2>
            <p className="mt-8 text-base md:text-lg text-charcoal/70 leading-relaxed max-w-2xl">
              At Kakkanat, healing is unhurried. Every oil, every meal, every
              gentle hand on a mother&apos;s shoulder is chosen with intention,
              shaped by classical Ayurveda and softened by quiet hospitality.
            </p>

            <div
              className="mt-10 flex items-start gap-5 p-6 md:p-8 rounded-2xl border border-sage/20 bg-white"
              data-testid="founder-villas-highlight"
            >
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sage/10 text-sage shrink-0">
                <Home size={20} strokeWidth={1.5} />
              </span>
              <div>
                <div className="font-serif text-xl text-charcoal">A Boutique Choice</div>
                <p className="text-sm md:text-base text-charcoal/65 mt-1.5 leading-relaxed">
                  We host only <strong className="text-charcoal">three private villas</strong> at
                  a time. Not because we cannot expand, but because undivided
                  attention cannot be scaled.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
